import { useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { Link, useSearchParams } from 'react-router-dom'
import moment from 'moment'

import routes from '../../routers'
import Layout from '../../components/Layout'
import { ROLES } from '../../utils/constants'
import { apiError } from '../../api/account'
import { redeemGift } from '../../api/gifts'
import { forgetGiftCode, pendingGiftCode, rememberGiftCode } from '../../utils/pendingGift'
import { getUserInfo } from '../../api/auth'
import { setUser } from '../../redux/actions/user'

import '../PurchaseBook/styles.css'
import styles from '../Gift/Gift.module.css'

const day = (d) => (d ? moment(d).format('MMMM D, YYYY') : '')
const PLAN_NAME = { pro: 'Morsel Pro', family: 'Morsel Family Pro' }

// The link in a gift email: sign in (or make a free account), then the gift starts
const RedeemGift = () => {
  const [query] = useSearchParams()
  const dispatch = useDispatch()
  const role = useSelector((state) => state.user?.userRole)
  const signedIn = role === ROLES.ROLE_USER
  const [code, setCode] = useState(query.get('code') || pendingGiftCode())
  const [result, setResult] = useState(null)
  const [error, setError] = useState('')
  const [busy, setBusy] = useState(false)

  // Not signed in yet: remember the code, so signing in brings them back here
  useEffect(() => {
    if (!signedIn && code) rememberGiftCode(code)
  }, [signedIn, code])

  const redeem = (e) => {
    e.preventDefault()
    setError('')
    setBusy(true)
    redeemGift(code)
      .then(({ data }) => {
        setResult(data)
        forgetGiftCode()
        // Their plan changed: refresh what the app knows about them
        getUserInfo()
          .then(({ data: user }) => user && dispatch(setUser(user)))
          .catch(() => {})
      })
      .catch((err) => setError(apiError(err, 'Something went wrong. Please try again.')))
      .finally(() => setBusy(false))
  }

  let body
  if (result) {
    body = (
      <section className="cbSection">
        <p className={styles.thanks}>Your gift is ready.</p>
        {result.kind === 'cookbook' ? (
          <>
            <h2 className={styles.big}>A ${Number(result.value).toFixed(2)} cookbook voucher</h2>
            <p>
              It’s saved on your account until {day(result.expires_at)}. When you order a printed
              cookbook, it’s taken off the price at checkout.
            </p>
            <Link className="cbNextButton" to={routes.purchaseBook}>
              Make a cookbook
            </Link>
          </>
        ) : (
          <>
            <h2 className={styles.big}>
              {result.extended ? 'A year added to your plan' : `${PLAN_NAME[result.kind]} is on`}
            </h2>
            <p>
              {result.extended
                ? `Your next payment moved a year later. You have Morsel through ${day(
                    result.until
                  )}.`
                : `You have ${PLAN_NAME[result.kind]} through ${day(result.until)}.`}
              {result.kind === 'family' && !result.extended
                ? ' You can invite up to 5 family members to share it from your account page.'
                : ''}
            </p>
            <Link className="cbNextButton" to={routes.addRecipe}>
              Add a recipe
            </Link>
          </>
        )}
      </section>
    )
  } else if (!signedIn) {
    body = (
      <section className="cbSection">
        <h2 className="cbSectionTitle">Someone sent you a gift</h2>
        <p className="cbHint">
          Create a free Morsel account, or sign in to yours, and the gift is added straight away.
          We’ll keep your code on this device.
        </p>
        <div className={styles.buttons}>
          <Link className="cbNextButton" to={routes.signUp}>
            Create a free account
          </Link>
          <Link className="cbTextLink" to={routes.signIn}>
            Sign in
          </Link>
        </div>
      </section>
    )
  } else {
    body = (
      <form className="cbSection" onSubmit={redeem}>
        <h2 className="cbSectionTitle">Open your gift</h2>
        <label className={styles.field}>
          Gift code
          <input
            className={`cbInput ${styles.code}`}
            value={code}
            onChange={(e) => setCode(e.target.value)}
            placeholder="XXXX-XXXX-XXXX-XXXX"
            autoComplete="off"
            maxLength={40}
            required
          />
        </label>
        {error ? <p className="cbError">{error}</p> : null}
        <button type="submit" className="cbNextButton" disabled={busy || !code.trim()}>
          {busy ? 'Opening…' : 'Open my gift'}
        </button>
      </form>
    )
  }

  return (
    <Layout title="Open your gift" allowGuests>
      <div className={`cbPage ${styles.page}`} style={{ maxWidth: 640 }}>
        <div className="cbIntro">
          <h1 className="cbTitle">A gift for you</h1>
        </div>
        {body}
      </div>
    </Layout>
  )
}

export default RedeemGift
